/**
 * The versions a product's changelog documents.
 *
 * The download list is built from GitHub releases, which is every build ever
 * published — twenty of them for the framework, most predating the changelog
 * and several superseded within a day. Offering all of them invites people to
 * install something nobody can tell them anything about.
 *
 * The changelog is the editorial list: a version appears there because someone
 * decided it was worth offering. Filtering the download list through it keeps
 * one decision in one place, rather than a second list to maintain in sync.
 */

/**
 * Version headings in a Keep-a-Changelog document.
 *
 * The pattern matches product-changelog.component's own, deliberately: if that
 * renders a heading as a version, this must agree, or a release could be
 * documented on the changelog page and still be missing from the downloads.
 *
 * `## [1.2.3] - 2024-01-15`, `## 1.2.3` and `## v1.2.3` all count.
 */
const VERSION_HEADING = /^##\s+\[?v?([\d.]+(?:-\w+)?)\]?(?:\s+-\s+(.+))?/;

export function parseChangelogVersions(markdown: string): string[] {
  const versions: string[] = [];
  for (const raw of markdown.split('\n')) {
    const match = raw.trim().match(VERSION_HEADING);
    if (match?.[1]) versions.push(match[1]);
  }
  return versions;
}

/**
 * Fetch a changelog and return the versions it documents.
 *
 * Returns null when the changelog cannot be read, which callers must treat as
 * "no opinion" rather than "nothing is documented". A failed fetch hiding every
 * download would turn a missing file into an outage.
 */
export async function fetchChangelogVersions(
  path: string
): Promise<string[] | null> {
  try {
    const response = await fetch(path);
    if (!response.ok) return null;
    return parseChangelogVersions(await response.text());
  } catch {
    return null;
  }
}
